import React, { useState, useEffect } from 'react';
import '../../../../assets/css/purchase/datatable.css';

// Components
import Breadcrumbs from '../../../components/Breadcrumbs';
import Topbar from '../../../components/Topbar';

// Date & Time
import moment from 'moment';

// Utils
import { getAlert, formatCurrency, parseCurrency, getDataWithComp } from '../../../../utils/SamuiUtils';
import { formatDate } from '../../../../utils/DateUtils';
import SearchAndFilter from '../../../components/Search/SearchAndFilter';
import Pagination from '../../../components/Footer/Pagination';
import FileModal from '../../../components/Modal/FileModal';

const PER_PAGE_KEY = 'productReceiptItemsPerPage';
const SELECTED_ROW_KEY = 'productReceiptSelectedRow';

const ProductReceiptTable = ({
    currentItems,
    onRowSelected,
    currentPage,
    setCurrentPage,
    handlePageChange,
    dataMasterList,
    itemsPerPage,
    setItemsPerPage,
    getReport,
    sortConfig,
    onSort,
}) => {
    const [itemsPerPageDropdown, setItemsPerPageDropdown] = useState(() => {
        const stored = Number(localStorage.getItem(PER_PAGE_KEY));
        return stored > 0 ? stored : Number(itemsPerPage) || 10;
    });
    const [selectedRow, setSelectedRow] = useState(() => localStorage.getItem(SELECTED_ROW_KEY));
    const [selectedRowIndex, setSelectedRowIndex] = useState(0);
    const [showFileModal, setShowFileModal] = useState(false);
    const [fileModalData, setFileModalData] = useState([]);

    useEffect(() => {
        setItemsPerPage(itemsPerPageDropdown);
        localStorage.setItem(PER_PAGE_KEY, String(itemsPerPageDropdown));
    }, [itemsPerPageDropdown, setItemsPerPage]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (
                document.activeElement.tagName === 'INPUT' ||
                document.activeElement.tagName === 'TEXTAREA' ||
                document.activeElement.tagName === 'SELECT'
            ) {
                return;
            }

            if (currentItems.length === 0) return;

            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    setSelectedRowIndex((prev) => {
                        const newIndex = prev > 0 ? prev - 1 : currentItems.length - 1;
                        const newRowId = currentItems[newIndex]?.Rec_No || newIndex + 1;
                        setSelectedRow(newRowId);
                        localStorage.setItem(SELECTED_ROW_KEY, newRowId);
                        return newIndex;
                    });
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    setSelectedRowIndex((prev) => {
                        const newIndex = prev < currentItems.length - 1 ? prev + 1 : 0;
                        const newRowId = currentItems[newIndex]?.Rec_No || newIndex + 1;
                        setSelectedRow(newRowId);
                        localStorage.setItem(SELECTED_ROW_KEY, newRowId);
                        return newIndex;
                    });
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (currentItems[selectedRowIndex]) {
                        onRowSelected(currentItems[selectedRowIndex].Rec_No);
                    }
                    break;
                case 'P':
                case 'p':
                    event.preventDefault();
                    if (currentItems[selectedRowIndex] && getReport) {
                        const row = currentItems[selectedRowIndex];
                        const status = row.Doc_Status ?? row.docStatus ?? row.DocStatus;
                        if (String(status) === '13') return;
                        getReport(currentItems[selectedRowIndex].Rec_No);
                    }
                    break;
                case 'ArrowLeft':
                    event.preventDefault();
                    if (currentPage > 1) {
                        handlePageChange(currentPage - 1);
                    }
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    const totalPages = Math.ceil(dataMasterList.length / itemsPerPageDropdown);
                    if (currentPage < totalPages) {
                        handlePageChange(currentPage + 1);
                    }
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentItems, selectedRowIndex, currentPage, dataMasterList.length, itemsPerPageDropdown, handlePageChange, onRowSelected, getReport]);

    useEffect(() => {
        if (selectedRow && currentItems.length > 0) {
            const index = currentItems.findIndex((item) => item.Rec_No === selectedRow);
            if (index !== -1) {
                setSelectedRowIndex(index);
            }
        }
    }, [selectedRow, currentItems]);

    const handleItemsPerPageChange = (event) => {
        const newVal = Number(event.target.value) || 10;
        setItemsPerPageDropdown(newVal);
        handlePageChange(1);
        setCurrentPage(1);
    };

    const handleViewFiles = (files) => {
        if (!Array.isArray(files) || files.length === 0) {
            return;
        }
        setFileModalData(files);
        setShowFileModal(true);
    };

    const startIndex = dataMasterList.length === 0 ? 0 : (currentPage - 1) * itemsPerPageDropdown + 1;
    const endIndex = Math.min(currentPage * itemsPerPageDropdown, dataMasterList.length);

    return (
        <div className="card table-card">
            <div className="table-responsive table-wrapper">
                <table className="table table-striped table-hover">
                    <thead className="thead-dark">
                        <tr>
                            <th className="text-center" style={{ width: '8%', cursor: 'pointer' }} onClick={() => onSort && onSort('Rec_Date')}>
                                <span className="d-inline-flex align-items-center justify-content-center w-100">
                                    วันที่ { /* arrow */ }
                                    {(!sortConfig || sortConfig.key !== 'Rec_Date') ? (
                                        <svg className="w-3.5 h-3.5 opacity-60 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 8l5-5 5 5H5zM15 12l-5 5-5-5h10z" /></svg>
                                    ) : sortConfig.direction === 'asc' ? (
                                        <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 12l5-5 5 5H5z" /></svg>
                                    ) : (
                                        <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8l-5 5-5-5h10z" /></svg>
                                    )}
                                </span>
                            </th>
                            <th className="text-center" style={{ width: '12%', cursor: 'pointer' }} onClick={() => onSort && onSort('Rec_No')}>
                                <span className="d-inline-flex align-items-center justify-content-center w-100">
                                    เลขที่เอกสาร {(!sortConfig || sortConfig.key !== 'Rec_No') ? (
                                        <svg className="w-3.5 h-3.5 opacity-60 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 8l5-5 5 5H5zM15 12l-5 5-5-5h10z" /></svg>
                                    ) : sortConfig.direction === 'asc' ? (
                                        <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 12l5-5 5 5H5z" /></svg>
                                    ) : (
                                        <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8l-5 5-5-5h10z" /></svg>
                                    )}
                                </span>
                            </th>
                            <th className="text-center" style={{ width: '10%', cursor: 'pointer' }} onClick={() => onSort && onSort('Ref_Doc')}>
                                <span className="d-inline-flex align-items-center justify-content-center w-100">
                                    เลขที่อ้างอิง {(!sortConfig || sortConfig.key !== 'Ref_Doc') ? (
                                        <svg className="w-3.5 h-3.5 opacity-60 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 8l5-5 5 5H5zM15 12l-5 5-5-5h10z" /></svg>
                                    ) : sortConfig.direction === 'asc' ? (
                                        <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 12l5-5 5 5H5z" /></svg>
                                    ) : (
                                        <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8l-5 5-5-5h10z" /></svg>
                                    )}
                                </span>
                            </th>
                            <th className="text-left" style={{ width: '20%', cursor: 'pointer' }} onClick={() => onSort && onSort('AP_Name')}>
                                <span className="d-inline-flex align-items-center">ชื่อผู้จำหน่าย {(!sortConfig || sortConfig.key !== 'AP_Name') ? (
                                    <svg className="w-3.5 h-3.5 opacity-60 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 8l5-5 5 5H5zM15 12l-5 5-5-5h10z" /></svg>
                                ) : sortConfig.direction === 'asc' ? (
                                    <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 12l5-5 5 5H5z" /></svg>
                                ) : (
                                    <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8l-5 5-5-5h10z" /></svg>
                                )}</span>
                            </th>
                            <th className="text-center" style={{ width: '10%' }}>สถานะเอกสาร</th>
                            <th className="text-center" style={{ width: '10%' }}>สถานะการจ่าย</th>
                            <th className="text-center" style={{ width: '10%', cursor: 'pointer' }} onClick={() => onSort && onSort('NetTotal')}>
                                <span className="d-inline-flex align-items-center justify-content-center w-100">
                                    ยอดรวมสุทธิ {(!sortConfig || sortConfig.key !== 'NetTotal') ? (
                                        <svg className="w-3.5 h-3.5 opacity-60 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 8l5-5 5 5H5zM15 12l-5 5-5-5h10z" /></svg>
                                    ) : sortConfig.direction === 'asc' ? (
                                        <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 12l5-5 5 5H5z" /></svg>
                                    ) : (
                                        <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8l-5 5-5-5h10z" /></svg>
                                    )}
                                </span>
                            </th>
                            <th className="text-center" style={{ width: '5%' }}>ไฟล์</th>
                            <th className="text-center" style={{ width: '5%' }}>แก้ไข</th>
                            <th className="text-center" style={{ width: '5%' }}>พิมพ์</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length > 0 ? (
                            currentItems.map((item, index) => (
                                <tr
                                    key={item.Rec_No || index}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: selectedRow === (item.Rec_No || index)
                                            ? 'rgb(105, 188, 216)'
                                            : 'transparent',
                                        transition: 'background-color 0.3s ease',
                                    }}
                                    onClick={() => {
                                        setSelectedRow(item.Rec_No || index);
                                        setSelectedRowIndex(index);
                                        localStorage.setItem(SELECTED_ROW_KEY, item.Rec_No || index);
                                    }}
                                >
                                    <td className="text-center">
                                        {item.Rec_Date ? formatDate(item.Rec_Date) : '-'}
                                    </td>
                                    <td className="text-center">{item.Rec_No || '-'}</td>
                                    <td className="text-center">{item.Ref_Doc || '-'}</td>
                                    <td className="text-left">{item.AP_Name || '-'}</td>
                                    <td className="text-center">
                                        {item.RecStatus_Name ? (
                                            <button
                                                className="btn"
                                                style={{
                                                    backgroundColor: item.RecStatus_Colour || '#6c757d',
                                                    color: 'white',
                                                    width: '100%',
                                                }}
                                            >
                                                {item.RecStatus_Name}
                                            </button>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td className="text-center">
                                        {item.DocStatusPaid_Name ? (
                                            <button
                                                className="btn"
                                                style={{
                                                    backgroundColor: item.DocStatusPaid_Colour || '#6c757d',
                                                    color: 'white',
                                                    width: '100%',
                                                }}
                                            >
                                                {item.DocStatusPaid_Name}
                                            </button>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td className="text-end">
                                        {formatCurrency(parseCurrency(item.NetTotal) || 0)}
                                    </td>
                                    <td className="text-center">
                                        {Array.isArray(item.File_Name) && item.File_Name.length > 0 ? (
                                            <button
                                                className="btn btn-link btn-sm px-3"
                                                onClick={() => handleViewFiles(item.File_Name)}
                                            >
                                                <i className="fas fa-paperclip" style={{ fontSize: '16px', color: 'rgb(239, 108, 0)' }}></i>
                                            </button>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td className="text-center">
                                        <button
                                            className="btn btn-link btn-sm px-3"
                                            onClick={() => onRowSelected(item.Rec_No)}
                                        >
                                            <i className="fa fa-edit" style={{ fontSize: '16px', color: 'rgb(239, 108, 0)' }}></i>
                                        </button>
                                    </td>
                                    <td className="text-center">
                                        {(() => {
                                            const status = item.Doc_Status ?? item.docStatus ?? item.DocStatus;
                                            const isCancelled = String(status) === '13';
                                            return (
                                                <button
                                                    className="btn btn-link btn-sm px-3"
                                                    onClick={() => {
                                                        if (isCancelled) return;
                                                        getReport(item.Rec_No);
                                                    }}
                                                    disabled={isCancelled}
                                                    style={isCancelled ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                                                >
                                                    <i className="fa fa-print" style={{ fontSize: '16px' }}></i>
                                                </button>
                                            );
                                        })()}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={10}>
                                    <center>
                                        <h5>ไม่พบข้อมูล</h5>
                                    </center>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="table-footer-sticky d-flex justify-content-between align-items-center p-3">
                <div>
                    <select
                        value={itemsPerPageDropdown}
                        onChange={handleItemsPerPageChange}
                        className="form-select"
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
                <div>
                    <span>
                        แสดง {startIndex}-{endIndex} ของ {dataMasterList.length.toLocaleString()} รายการ
                    </span>
                </div>
                <div className="card-footer bg-light p-2">
                    <small className="text-muted">
                        <i className="fas fa-keyboard me-1"></i>
                        <strong>คีย์ลัด:</strong> ↑↓ เลือกแถว | ←→ เปลี่ยนหน้า | Enter เข้าดู | P พิมพ์
                    </small>
                </div>
                <Pagination
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    itemsPerPage={itemsPerPageDropdown}
                    dataMasterList={dataMasterList}
                    handlePageChange={handlePageChange}
                />
            </div>

            {showFileModal && (
                <FileModal
                    mode="S"
                    showViewAttachModal={showFileModal}
                    setShowViewAttachModal={setShowFileModal}
                    selectedFiles={fileModalData}
                    selectedRecFiles={[]}
                />
            )}
        </div>
    );
};

function Main({ masterList, detailList, statusColours, name, onPageInsert, onRowSelected, getReport }) {
    const [dataMasterList, setDataMasterList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(() => {
        const stored = Number(localStorage.getItem(PER_PAGE_KEY));
        return stored > 0 ? stored : Number(localStorage.getItem('itemsPerPageDropdown')) || 10;
    });
    const [searchTerm, setSearchTerm] = useState(localStorage.getItem('searchTerm') || '');
    const [filteredItems, setFilteredItems] = useState([]);

    const [startDate, setStartDate] = useState(() => {
        const storedStartDate = localStorage.getItem("startDate");
        return storedStartDate || moment().startOf("month").format("YYYY-MM-DD");
    });
    const [endDate, setEndDate] = useState(() => {
        const storedEndDate = localStorage.getItem("endDate");
        return storedEndDate || moment().endOf("month").format("YYYY-MM-DD");
    });

    const [dateType, setDateType] = useState('1');
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    useEffect(() => {
        initialize();
    }, [masterList, detailList]);

    useEffect(() => {
        if (dataMasterList && dataMasterList.length > 0) {
            setCurrentPage(1);
            filterItems();
            localStorage.setItem("searchTerm", searchTerm);
        }
    }, [searchTerm, dataMasterList, selectedStatus]);

    useEffect(() => {
        if (dataMasterList && dataMasterList.length > 0) {
            setCurrentPage(1);
            filterItems();
            localStorage.setItem("startDate", startDate);
            localStorage.setItem("endDate", endDate);
        }
    }, [startDate, endDate, dataMasterList, selectedStatus]);

    const initialize = async () => {
        try {
            if (masterList && masterList.length > 0) {
                setDataMasterList(masterList);

                const image = await getDataWithComp(
                    "Upload_File_List",
                    `AND DocCode_ID = 3`
                );
                const updatedMasterList = masterList.map(item => ({
                    ...item,
                    File_Name: image
                        .filter(img => img.Doc_ID === item.Rec_Id)
                        .map(img => img.File_Name),
                }));
                setFilteredItems(updatedMasterList);
                setCurrentPage(1);
            }
        } catch (error) {
            getAlert('FAILED', error.message);
        }
    };

    const filterItems = () => {
        try {
            let filtered = masterList || [];
            const start = moment(startDate, "YYYY-MM-DD");
            const end = moment(endDate, "YYYY-MM-DD");

            filtered = filtered.filter(item => {
                const itemDate = dateType === '1'
                    ? moment(item.Rec_Date, "YYYY-MM-DD")
                    : moment(item.Rec_DueDate, "YYYY-MM-DD");
                return itemDate.isBetween(start, end, null, '[]');
            });

            if (searchTerm.trim() !== '') {
                const lowercasedSearchTerm = searchTerm.toLowerCase();
                filtered = filtered.filter(item =>
                    Object.values(item).some(value => {
                        if (typeof value === 'string') {
                            return value.toLowerCase().includes(lowercasedSearchTerm);
                        } else if (typeof value === 'number') {
                            return value.toString().includes(searchTerm);
                        }
                        return false;
                    })
                );
            }

            if (selectedStatus) {
                filtered = filtered.filter((item) => item.RecStatus_Name === selectedStatus);
            }

            setFilteredItems(filtered);
        } catch (error) {
            console.error('Filter error:', error.message);
            setFilteredItems([]);
        }
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const sortedItems = React.useMemo(() => {
        if (!sortConfig || !sortConfig.key) return filteredItems;
        const key = sortConfig.key;
        const direction = sortConfig.direction === 'desc' ? -1 : 1;
        const copy = [...filteredItems];
        copy.sort((a, b) => {
            const av = a[key];
            const bv = b[key];

            const dateKeys = ['Rec_Date'];
            if (dateKeys.includes(key)) {
                const am = av ? moment(av) : null;
                const bm = bv ? moment(bv) : null;
                if (!am && !bm) return 0;
                if (!am) return 1 * direction;
                if (!bm) return -1 * direction;
                if (am.isBefore(bm)) return -1 * direction;
                if (am.isAfter(bm)) return 1 * direction;
                return 0;
            }

            const numKeys = ['NetTotal'];
            if (numKeys.includes(key)) {
                const an = parseCurrency(av) || 0;
                const bn = parseCurrency(bv) || 0;
                return an === bn ? 0 : an < bn ? -1 * direction : 1 * direction;
            }

            const as = (av ?? '').toString().toLowerCase();
            const bs = (bv ?? '').toString().toLowerCase();
            if (as < bs) return -1 * direction;
            if (as > bs) return 1 * direction;
            return 0;
        });
        return copy;
    }, [filteredItems, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
        setCurrentPage(1);
    };

    return (
        <>
            <Topbar
                breadcrumbs={[
                    { label: 'จัดซื้อสินค้า', link: '/product-receipt' },
                    { label: name },
                ]}
            />
            <div className="page-header d-flex justify-content-between align-items-center mb-0">
                <Breadcrumbs
                    page={name}
                    items={[
                        { name: 'จัดซื้อสินค้า', url: '/product-receipt' },
                        { name: name, url: '/product-receipt' },
                    ]}
                />
            </div>
            <div className="row">
                <div className="col-12">
                    <SearchAndFilter
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        searchPlaceholder="ค้นหาเลขเอกสาร, ผู้จำหน่าย, พนักงาน, แผนก..."
                        startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        showDateFilter={true}
                        onAddNew={onPageInsert}
                        addButtonText={`สร้าง${name}`}
                        showAddButton={true}
                        name={name}
                        filteredItems={filteredItems}
                    />
                    <ProductReceiptTable
                        currentItems={currentItems}
                        onRowSelected={onRowSelected}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        handlePageChange={handlePageChange}
                        dataMasterList={sortedItems}
                        itemsPerPage={itemsPerPage}
                        setItemsPerPage={setItemsPerPage}
                        getReport={(docNo) => getReport('rec', docNo)}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                    />
                </div>
            </div>
        </>
    );
}

export default Main;
